import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { fetchWithRefresh } from '../utils/auth';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import { useUser } from '../contexts/UserContext';
import { useUrl } from '../contexts/UrlContext';
import { getRecentSubdomainChange, clearSubdomainChangeData, dispatchSubdomainChanged } from '../utils/subdomainEvents';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { setCurrentSubdomain } from '../redux/slices/languageSlice';
import { extractSubdomain } from '../utils/auth';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { menuService, schoolService } from '../utils/api';
import { validateSubdomain } from '../utils/subdomainValidator';

const SchoolSelectorDashboard = () => {
  const { t, i18n } = useTranslation();
  const { user, updateUser, refreshUser } = useUser();
  const { getAdminUrl, getBaseUrl } = useUrl();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [schoolToDelete, setSchoolToDelete] = useState(null);
  const [createFormData, setCreateFormData] = useState({
    name: '',
    subdomain: '',
    homepageUrl: '',
    language: '' // This will be set automatically to the current UI language when creating a school
  });
  const [createError, setCreateError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [notification, setNotification] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  
  // Ref to track component mount status
  const isMounted = useRef(false);
  // Track render count for debugging
  const renderCount = useRef(0);
  // Generate unique timestamp for cache busting
  const cacheBustTimestamp = useRef(Date.now());
  
  // For subdomain validation
  const [subdomainStatus, setSubdomainStatus] = useState(null);
  const [isCheckingSubdomain, setIsCheckingSubdomain] = useState(false);
  const subdomainTimeoutRef = useRef(null);
  
  // Logging component lifecycle
  useEffect(() => {
    isMounted.current = true;
    console.log('[SchoolSelector] Component mounted');
    
    // Log query cache state on mount
    const cachedData = queryClient.getQueryData(['userSchools']);
    console.log('[SchoolSelector] Initial cached schools data:', 
      cachedData ? `${cachedData.length} schools in cache` : 'No cached data');
    
    return () => {
      console.log('[SchoolSelector] Component unmounting');
      isMounted.current = false;
    };
  }, [queryClient]);
  
  // Log each render
  useEffect(() => {
    renderCount.current += 1;
    console.log(`[SchoolSelector] Render #${renderCount.current}, pathname: ${location.pathname}`);
  });

  // Function to create a fetch function with cache-busting
  const createNoCacheFetch = useCallback(() => {
    return async () => {
      const startTime = Date.now();
      console.log('[SchoolSelector] Fetching user schools data with React Query');
      
      try {
        // Add cache busting timestamp to URL
        const timestamp = Date.now();
        const url = `/api/user/schools?_t=${timestamp}`;
        
        // Use fetchWithRefresh but add cache-control headers
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          cache: 'no-store' // Modern fetch cache control
        });
      
      if (!response.ok) {
          console.error(`[SchoolSelector] API error: ${response.status} ${response.statusText}`);
        throw new Error(`Failed to fetch user schools: ${response.status}`);
      }

      const data = await response.json();
        const elapsed = Date.now() - startTime;
        
        console.log(`[SchoolSelector] Successfully fetched ${data.length} schools in ${elapsed}ms`, 
          `(request timestamp: ${timestamp})`);
        console.log('[SchoolSelector] School IDs received:', data.map(s => s.id).join(', '));
        
        // This is important for debugging
        if (data.length === 0) {
          console.warn('[SchoolSelector] API returned empty schools array - this might be unexpected');
        }
        
        return data;
      } catch (error) {
        console.error('[SchoolSelector] Error in query function:', error);
        throw error;
      }
    };
  }, []);

  // Use React Query to fetch schools
  const { 
    data: schools = [], 
    isLoading: loading, 
    error: fetchError,
    refetch: refetchSchools,
    dataUpdatedAt
  } = useQuery({
    queryKey: ['userSchools', cacheBustTimestamp.current], // Add timestamp to ensure unique key
    queryFn: createNoCacheFetch(),
    // Disable automatic refetching to prevent race conditions
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    staleTime: 0, // Always fetch fresh data
    cacheTime: 1000 * 60, // Only cache for 1 minute
    retry: 2,
    onSuccess: (data) => {
      console.log(`[SchoolSelector] Query success: ${data.length} schools loaded`);
    },
    onError: (error) => {
      console.error('[SchoolSelector] Query error:', error);
    }
  });
  
  // Log when data changes
  useEffect(() => {
    if (dataUpdatedAt) {
      console.log(`[SchoolSelector] School data updated at: ${new Date(dataUpdatedAt).toLocaleTimeString()}`);
      console.log(`[SchoolSelector] Current schools count: ${schools.length}`);
    }
  }, [schools, dataUpdatedAt]);
  
  // Log location changes to debug navigation effects
  useEffect(() => {
    console.log(`[SchoolSelector] Location changed: ${location.pathname}`);
    
    // If coming back to the dashboard, force refetch
    if (location.pathname === '/dashboard') {
      console.log('[SchoolSelector] Back on dashboard, forcing refetch');
      // Force refetch with a slight delay to ensure component is fully mounted
      setTimeout(() => {
        if (isMounted.current) {
          console.log('[SchoolSelector] Executing delayed refetch');
          // Update cache bust timestamp to force a new request
          cacheBustTimestamp.current = Date.now();
          // Remove cached data to ensure fresh fetch
          queryClient.removeQueries({ queryKey: ['userSchools'] });
          refetchSchools();
        }
      }, 300);
    }
  }, [location, queryClient, refetchSchools]);

  // Create school mutation
  const createSchoolMutation = useMutation({
    mutationFn: async (schoolData) => {
      console.log('[SchoolSelector] Creating new school:', schoolData);
      const response = await fetchWithRefresh('/api/admin/schools', {
        method: 'POST',
        body: JSON.stringify(schoolData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to create school');
      }

      return await response.json();
    },
    onSuccess: async (data) => {
      console.log('[SchoolSelector] School created successfully:', data);
      toast.success('School created successfully!');
      setShowCreateModal(false);
      
      // Important: invalidate all school-related queries to ensure fresh data
      console.log('[SchoolSelector] Invalidating queries after school creation');
      await queryClient.invalidateQueries({ queryKey: ['userSchools'] });
      console.log('[SchoolSelector] Queries invalidated, cleaning cache');
      queryClient.removeQueries({ queryKey: ['userSchools'] });
      
      // Update cache bust timestamp to ensure next fetch is fresh
      cacheBustTimestamp.current = Date.now();
      
      // Wait a moment for the invalidation to process
      setTimeout(() => {
        // Force a refresh of user data to ensure roles are up to date
        console.log('[SchoolSelector] Refreshing user data after timeout');
        refreshUser().then((userData) => {
          // Store current user info and created school info in session storage for debugging
          const schoolInfo = {
            id: data.id,
            name: data.name,
            subdomain: createFormData.subdomain,
            timestamp: Date.now(),
            // Add detailed role information for debugging
            userRoles: userData?.schools?.find(s => s.id === data.id)?.roles || [],
            userInfo: {
              id: userData?.id,
              hasNewSchool: !!userData?.schools?.find(s => s.id === data.id)
            }
          };
          
          // Store debugging info in session storage
          sessionStorage.setItem('lastCreatedSchool', JSON.stringify(schoolInfo));
          console.log('[SchoolSelector] Stored school creation info for debugging:', schoolInfo);
          
          // Store the current user for role comparison
          try {
            sessionStorage.setItem('userBeforeRedirect', JSON.stringify({
              id: userData?.id,
              schools: userData?.schools?.map(s => ({ 
                id: s.id, 
                name: s.name,
                subdomain: s.subdomain,
                roles: s.roles 
              })),
              timestamp: Date.now()
            }));
          } catch (e) {
            console.error('[SchoolSelector] Error saving user data:', e);
          }
          
          // Redirect to the new school's admin page
          const subdomain = createFormData.subdomain;
          console.log(`[SchoolSelector] Redirecting to new school settings: ${subdomain}`);
          window.location.href = getSchoolAdminUrl(subdomain) + '/settings';
        });
      }, 500);
    },
    onError: (err) => {
      console.error('[SchoolSelector] Error creating school:', err);
      setCreateError(err.message || 'Failed to create school');
    }
  });

  // Delete school mutation
  const deleteSchoolMutation = useMutation({
    mutationFn: async (schoolId) => {
      console.log('[SchoolSelector] Deleting school:', schoolId);
      const response = await fetchWithRefresh(`/api/admin/schools/${schoolId}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete school');
      }
      
      return await response.json();
    },
    onSuccess: () => {
      toast.success('School deleted successfully');
      setShowDeleteModal(false);
      setSchoolToDelete(null);
      
      // Invalidate school-related queries to ensure fresh data
      console.log('[SchoolSelector] Invalidating queries after school deletion');
      queryClient.invalidateQueries({ queryKey: ['userSchools'] });
      // Update cache bust timestamp to ensure next fetch is fresh
      cacheBustTimestamp.current = Date.now();
      // Force refetch after invalidation
      setTimeout(() => {
        if (isMounted.current) {
          console.log('[SchoolSelector] Executing refetch after deletion');
          refetchSchools();
        }
      }, 300);
    },
    onError: (err) => {
      console.error('[SchoolSelector] Error deleting school:', err);
      setDeleteError(err.message || 'Failed to delete school');
    }
  });

  // Function to refresh user data and schools list
  const refreshUserSchools = useCallback(async () => {
    try {
      console.log('[SchoolSelector] Manually refreshing user schools data');
      
      // First refresh user data to ensure it's up to date
      await refreshUser();
      console.log('[SchoolSelector] User data refreshed, now invalidating school queries');
      
      // Clear cache and refetch
      queryClient.removeQueries({ queryKey: ['userSchools'] });
      
      // Update cache bust timestamp to ensure next fetch is fresh
      cacheBustTimestamp.current = Date.now();
      
      // Then fetch the latest schools
      console.log('[SchoolSelector] Executing manual refetch');
      const result = await refetchSchools();
      
      console.log('[SchoolSelector] Manual refresh result:', 
        result.isSuccess ? `Success: ${result.data.length} schools` : `Failed: ${result.error}`);
      
      return result;
    } catch (err) {
      console.error('[SchoolSelector] Error refreshing user schools:', err);
      return { isSuccess: false, error: err };
    }
  }, [refreshUser, refetchSchools, queryClient]);

  // Listen for subdomain change events to refresh data
  useEffect(() => {
    const handleSubdomainChange = async (event) => {
      // Extract the school data from the event
      const { detail } = event;
      if (!detail || !detail.newSubdomain) {
        console.error('Cannot change subdomain: No subdomain data provided in event');
        return;
      }

      const newSubdomain = detail.newSubdomain;
      const previousSubdomain = detail.previousSubdomain;
      
      setIsRedirecting(true);

      try {
        const currentSubdomain = extractSubdomain();
        
        console.log(`[SchoolSelector] Changing subdomain from ${currentSubdomain} to ${newSubdomain}`);
        
        // Set flag to indicate this is a subdomain switch, not a navigation away from subdomain
        sessionStorage.setItem('isSubdomainSwitching', 'true');
        
        // Update Redux store
        dispatch(setCurrentSubdomain(newSubdomain));
        
        // Check if we're on a settings page or a tab where we should stay
        const currentPath = window.location.pathname;
        if (currentPath.includes('/admin/settings')) {
          // For settings pages, we want to stay on the same tab but change subdomain
          console.log(`[SchoolSelector] On settings page, keeping the same view but updating subdomain`);
          
          // Update the URL without forcing a page reload or navigation
          const newPath = currentPath.replace(`/${previousSubdomain}/`, `/${newSubdomain}/`);
          window.history.pushState({}, '', newPath);
          
          // Manually trigger a refresh of relevant data
          const refreshResult = await refreshUserSchools();
          console.log('[SchoolSelector] Refresh after subdomain change:', 
            refreshResult.isSuccess ? 'Success' : `Failed: ${refreshResult.error}`);
          
          setIsRedirecting(false);
        } else {
          // For other pages, redirect to the admin dashboard
          const redirectUrl = `/${newSubdomain}/admin`;
          console.log(`[SchoolSelector] Redirecting to: ${redirectUrl}`);
          navigate(redirectUrl);
        }
      } catch (error) {
        console.error('Error during subdomain change:', error);
        setIsRedirecting(false);
        // Display error notification
        setNotification({
          type: 'error',
          message: t('errors.subdomain_change_failed')
        });
      }
    };
    
    // Register event listener
    window.addEventListener('subdomain-changed', handleSubdomainChange);
    
    // Check if we need to refresh on mount
    const subdomainChange = getRecentSubdomainChange(10000); // Check for changes in the last 10 seconds
    if (subdomainChange) {
      console.log('[SchoolSelector] Detected recent subdomain change, refreshing data');
      refreshUserSchools();
      // Clear the subdomain change data after handling it
      clearSubdomainChangeData();
    }
    
    // Check if coming back after school creation
    const lastCreatedSchool = sessionStorage.getItem('lastCreatedSchool');
    if (lastCreatedSchool) {
      try {
        const schoolInfo = JSON.parse(lastCreatedSchool);
        const creationTime = schoolInfo.timestamp || 0;
        const timeSinceCreation = Date.now() - creationTime;
        
        // If created within the last 30 seconds and we're on the dashboard
        if (timeSinceCreation < 30000 && location.pathname === '/dashboard') {
          console.log('[SchoolSelector] Detected return after recent school creation:', schoolInfo);
          console.log('[SchoolSelector] Time since creation:', Math.round(timeSinceCreation/1000), 'seconds');
          
          // Force cache clear and data refresh
          console.log('[SchoolSelector] Forcing cache reset and data refresh');
          queryClient.removeQueries({ queryKey: ['userSchools'] });
          // Update cache bust timestamp to ensure next fetch is fresh
          cacheBustTimestamp.current = Date.now();
          setTimeout(() => {
            if (isMounted.current) {
              refetchSchools();
              // Clear the stored info after handling
              sessionStorage.removeItem('lastCreatedSchool');
            }
          }, 300);
        } else if (timeSinceCreation >= 30000) {
          // Clean up old data
          sessionStorage.removeItem('lastCreatedSchool');
        }
      } catch (e) {
        console.error('[SchoolSelector] Error parsing lastCreatedSchool:', e);
        sessionStorage.removeItem('lastCreatedSchool');
      }
    }
    
    // Cleanup event listener
    return () => {
      window.removeEventListener('subdomain-changed', handleSubdomainChange);
    };
  }, [dispatch, navigate, refreshUserSchools, t, location.pathname, queryClient, refetchSchools]);

  // Helper to get admin URL for a school using UrlContext
  const getSchoolAdminUrl = (subdomain) => {
    // Check if this is a recently updated subdomain
    const subdomainChange = getRecentSubdomainChange();
    
    // If this is the old subdomain that was just changed, use the new one instead
    if (subdomainChange && subdomainChange.previousSubdomain === subdomain && subdomainChange.newSubdomain) {
      console.log(`[SchoolSelector] Using updated subdomain: ${subdomain} -> ${subdomainChange.newSubdomain}`);
      return getAdminUrl(subdomainChange.newSubdomain);
    }
    
    return getAdminUrl(subdomain);
  };

  // Helper to get public URL for a school using UrlContext - same logic as above
  const getSchoolPublicUrl = (subdomain) => {
    // Check if this is a recently updated subdomain
    const subdomainChange = getRecentSubdomainChange();
    
    // If this is the old subdomain that was just changed, use the new one instead
    if (subdomainChange && subdomainChange.previousSubdomain === subdomain && subdomainChange.newSubdomain) {
      console.log(`[SchoolSelector] Using updated subdomain: ${subdomain} -> ${subdomainChange.newSubdomain}`);
      return getBaseUrl(subdomainChange.newSubdomain);
    }
    
    return getBaseUrl(subdomain);
  };

  // Function to create a new school
  const handleCreateSchool = async () => {
    try {
      setCreateError('');

      // Basic validation
      if (!createFormData.name || !createFormData.subdomain) {
        setCreateError('School name and subdomain are required');
        return;
      }

      // Comprehensive subdomain validation
      const validationResult = validateSubdomain(createFormData.subdomain);
      if (!validationResult.valid) {
        setCreateError(validationResult.message);
        return;
      }

      // Check if subdomain is available (additional server-side check)
      if (subdomainStatus && !subdomainStatus.available) {
        setCreateError(subdomainStatus.message || 'This subdomain is not available');
        return;
      }

      // Get current UI language
      const currentLanguage = i18n.language;
      
      // Create request body with language and normalized subdomain
      const requestBody = {
        ...createFormData,
        subdomain: validationResult.normalizedSubdomain,
        language: currentLanguage
      };

      console.log('[SchoolSelector] Creating school with data:', requestBody);
      
      // Use the mutation to create the school
      createSchoolMutation.mutate(requestBody);
    } catch (err) {
      console.error('Error creating school:', err);
      setCreateError(err.message || 'Failed to create school');
    }
  };

  // Handle subdomain input changes with debounced validation
  const handleSubdomainChange = (e) => {
    const newSubdomain = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    
    // Update form data
    setCreateFormData({...createFormData, subdomain: newSubdomain});
    
    // If no subdomain, clear status
    if (!newSubdomain) {
      setSubdomainStatus(null);
      setIsCheckingSubdomain(false);
      return;
    }
    
    // Clear any existing timeout
    if (subdomainTimeoutRef.current) {
      clearTimeout(subdomainTimeoutRef.current);
    }
    
    // Validate locally first
    const validationResult = validateSubdomain(newSubdomain);
    if (!validationResult.valid) {
      setSubdomainStatus({ 
        available: false, 
        message: validationResult.message
      });
      setIsCheckingSubdomain(false);
      return;
    }
    
    // If local validation passes, check server-side after a delay
    setIsCheckingSubdomain(true);
    
    subdomainTimeoutRef.current = setTimeout(async () => {
      try {
        // For the subdomain check, we need a school ID - for a new school, we can use a special value
        const result = await schoolService.checkSubdomainAvailability(newSubdomain, 'new');
        setSubdomainStatus(result);
      } catch (err) {
        console.error('Error checking subdomain availability:', err);
        setSubdomainStatus({
          available: false,
          message: 'Error checking subdomain availability'
        });
      } finally {
        setIsCheckingSubdomain(false);
      }
    }, 500); // 500ms debounce
  };
  
  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (subdomainTimeoutRef.current) {
        clearTimeout(subdomainTimeoutRef.current);
      }
    };
  }, []);

  // Function to handle school deletion
  const handleDeleteSchool = async () => {
    if (!schoolToDelete) return;
    
    try {
      setDeleteError('');
      console.log('[SchoolSelector] Initiating deletion of school:', schoolToDelete.id, schoolToDelete.name);
      
      // Use the mutation to delete the school
      deleteSchoolMutation.mutate(schoolToDelete.id);
    } catch (err) {
      console.error('Error deleting school:', err);
      setDeleteError(err.message || 'Failed to delete school');
    }
  };

  // Function to generate badge color based on role
  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Superadmin':
        return 'bg-purple-100 text-purple-800';
      case 'Admin':
        return 'bg-blue-100 text-blue-800';
      case 'Editor':
        return 'bg-green-100 text-green-800';
      case 'Viewer':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper to get icon and text color for subdomain status
  const getSubdomainStatusColor = () => {
    if (!subdomainStatus) return {};
    
    if (subdomainStatus.available) {
      return { 
        iconColor: 'text-green-500',
        textColor: 'text-green-700',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      };
    } else {
      return { 
        iconColor: 'text-red-500',
        textColor: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200'
      };
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isAdmin={true} />
      <div className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {t('schoolSelectorTitle', 'Your Schools')}
              </h1>
              <p className="mt-2 text-sm text-gray-500">
                {t('schoolSelectorDescription', 'Select a school to manage its menus and settings')}
              </p>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              {t('createNewSchool', 'Create New School')}
            </button>
          </div>

          {loading && (
            <div className="flex justify-center py-10">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          )}

          {fetchError && !loading && (
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-9v4a1 1 0 11-2 0v-4a1 1 0 112 0zm0-4a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">
                    {fetchError.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!loading && !fetchError && schools.length === 0 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-9v4a1 1 0 11-2 0v-4a1 1 0 112 0zm0-4a1 1 0 11-2 0 1 1 0 012 0z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    {t('noSchoolsFound', 'You do not have access to any schools yet.')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {!loading && !fetchError && schools.length > 0 && (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {schools.map(school => (
                <div 
                  key={school.id} 
                  className="bg-white shadow-md rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900">{school.name}</h3>
                    
                    {/* Role badges */}
                    <div className="mt-2 flex flex-wrap gap-2">
                      {school.roles?.map(role => (
                        <span 
                          key={role} 
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(role)}`}
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                    
                    {school.homepage_url && (
                      <p className="text-sm text-gray-500 truncate mt-1">{school.homepage_url}</p>
                    )}
                    
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Link
                        to={getSchoolAdminUrl(school.subdomain)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        onClick={() => {
                          // Store school access debug info
                          sessionStorage.setItem('schoolAccess', JSON.stringify({
                            timestamp: Date.now(),
                            schoolId: school.id,
                            schoolName: school.name,
                            subdomain: school.subdomain,
                            userRoles: school.roles || []
                          }));
                          console.log(`[SchoolSelector] Storing access info for school: ${school.name} with roles: ${school.roles?.join(', ')}`);
                        }}
                      >
                        {t('accessAdmin', 'Access Admin')}
                      </Link>
                      
                      <a
                        href={getSchoolPublicUrl(school.subdomain)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                      >
                        {t('viewPublicSite', 'View Site')}
                      </a>
                      
                      {/* Only show delete button for schools where user is Superadmin */}
                      {school.roles?.includes('Superadmin') && (
                        <button
                          onClick={() => {
                            setSchoolToDelete(school);
                            setShowDeleteModal(true);
                          }}
                          className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md shadow-sm text-red-700 bg-white hover:bg-red-50"
                          disabled={deleteSchoolMutation.isPending}
                        >
                          {t('delete', 'Delete')}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create School Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">{t('createNewSchool', 'Create New School')}</h2>
            
            {createError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">
                {createError}
              </div>
            )}
            
            <form onSubmit={(e) => { e.preventDefault(); handleCreateSchool(); }}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('schoolName', 'School Name')} *
                </label>
                <input 
                  type="text"
                  value={createFormData.name}
                  onChange={(e) => setCreateFormData({...createFormData, name: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  required
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('subdomain', 'Subdomain')} *
                </label>
                <div className="flex items-center">
                  <span className="bg-gray-100 p-2 border border-r-0 border-gray-300 rounded-l-md text-gray-500">
                    https://schoolmenus.xyz/
                  </span>
                  <input 
                    type="text"
                    value={createFormData.subdomain}
                    onChange={handleSubdomainChange}
                    className={`w-full p-2 border ${subdomainStatus ? (subdomainStatus.available ? 'border-green-300' : 'border-red-300') : 'border-gray-300'} rounded-r-md`}
                    placeholder="yourschool"
                    required
                  />
                </div>
                
                {/* Display availability status with appropriate colors */}
                {isCheckingSubdomain && (
                  <div className="mt-1 text-sm text-gray-500 flex items-center">
                    <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {t('checkingAvailability', 'Checking availability...')}
                  </div>
                )}
                
                {!isCheckingSubdomain && subdomainStatus && (
                  <div className={`mt-1 text-sm ${getSubdomainStatusColor().textColor} flex items-center`}>
                    {subdomainStatus.available ? (
                      <svg className={`h-4 w-4 mr-1 ${getSubdomainStatusColor().iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className={`h-4 w-4 mr-1 ${getSubdomainStatusColor().iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    {subdomainStatus.message}
                  </div>
                )}
                
                {!subdomainStatus && (
                  <p className="mt-1 text-sm text-gray-500">
                    {t('subdomainHelp', 'Only lowercase letters, numbers, and hyphens. Minimum 3 characters.')}
                  </p>
                )}
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('homepageUrl', 'School Website URL')} (optional)
                </label>
                <input 
                  type="url"
                  value={createFormData.homepageUrl}
                  onChange={(e) => setCreateFormData({...createFormData, homepageUrl: e.target.value})}
                  className="w-full p-2 border border-gray-300 rounded-md"
                  placeholder="https://yourschool.edu"
                />
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateFormData({ name: '', subdomain: '', homepageUrl: '' });
                    setSubdomainStatus(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  disabled={createSchoolMutation.isPending}
                >
                  {t('cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  disabled={createSchoolMutation.isPending || isCheckingSubdomain || (subdomainStatus && !subdomainStatus.available)}
                >
                  {createSchoolMutation.isPending ? t('creating', 'Creating...') : t('createSchool', 'Create School')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete School Modal */}
      {showDeleteModal && schoolToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h2 className="text-xl font-bold mb-4">{t('deleteSchool', 'Delete School')}</h2>
            
            <p className="mb-4">
              {t('deleteSchoolConfirmation', 'Are you sure you want to delete')} <span className="font-semibold">{schoolToDelete.name}</span>?
              {t('deleteSchoolWarning', 'This action cannot be undone.')}
            </p>
            
            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md border border-red-200">
                {deleteError}
              </div>
            )}
            
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSchoolToDelete(null);
                  setDeleteError('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                disabled={deleteSchoolMutation.isPending}
              >
                {t('cancel', 'Cancel')}
              </button>
              <button
                onClick={handleDeleteSchool}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                disabled={deleteSchoolMutation.isPending}
              >
                {deleteSchoolMutation.isPending ? t('deleting', 'Deleting...') : t('deleteSchool', 'Delete School')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchoolSelectorDashboard; 